import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { CustomerStorage, AdminStorage, GuestIntent, hadPriorCustomerSession } from '../utils/storage';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  // On initial mount, optimistically restore the cached display object
  // (for instant UI), then verify it against the httpOnly-cookie session
  // via GET /auth/session. The cookie itself is unreadable from JS, so
  // this network check is the only real source of truth.
  useEffect(() => {
    // CRITICAL: If Google OAuth is redirecting back to /auth/google/callback,
    // skip the hydration check entirely. AuthCallback will exchange the code,
    // the backend will set cookies, and it will navigate — no need to
    // restore session here first.
    if (window.location.pathname === '/auth/google/callback') {
      setLoading(false);
      return;
    }

    // AuthProvider wraps the whole app, but the admin area has its own
    // cookie-based session (AdminLayout) — skip the customer session
    // probe there entirely so every admin page load doesn't fire a
    // pointless /auth/session -> /auth/refresh pair of 401s.
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }

    const cachedUser = localStorage.getItem('user');
    const cachedGuest = localStorage.getItem('guest');
    if (cachedUser) {
      setUser(JSON.parse(cachedUser));
      setIsGuest(false);
    } else if (cachedGuest) {
      setUser(JSON.parse(cachedGuest));
      setIsGuest(true);
    }

    const applySession = (data) => {
      if (data.is_guest) {
        localStorage.setItem('guest', JSON.stringify(data.guest));
        localStorage.removeItem('user');
        setUser(data.guest);
        setIsGuest(true);
      } else {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.removeItem('guest');
        setUser(data.user);
        setIsGuest(false);
      }
    };

    // Skip the interceptor's own retry loop here — we handle the
    // refresh-then-retry explicitly below so a truly logged-out visitor
    // doesn't bounce through two rounds of 401s.
    axios.get(`${API}/auth/session`, { withCredentials: true, _skipAuthRefresh: true })
      .then(({ data }) => applySession(data))
      .catch(async () => {
        // The 24h access-token cookie may have expired while the 30-day
        // refresh-token cookie is still valid — try one explicit refresh
        // before giving up on the session.
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true, _skipAuthRefresh: true });
          const { data } = await axios.get(`${API}/auth/session`, { withCredentials: true, _skipAuthRefresh: true });
          applySession(data);
        } catch {
          // No valid cookie session — clear any stale cached display object.
          CustomerStorage.clear();
          setUser(null);
          setIsGuest(false);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // Snapshot BEFORE clearAuthOnly() wipes it — tells us whether a
    // (possibly different) user was already signed in on this device.
    const priorSession = hadPriorCustomerSession();

    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });

    // CRITICAL FIX: Clear stale auth before setting new login.
    // Use clearAuthOnly() so bookingIntent / hoopbook_selection survive -
    // the whole point of the pre-login intent is to resume after login.
    CustomerStorage.clearAuthOnly();

    // SECURITY: if someone else was already logged in on this device
    // (no explicit logout happened), any leftover GuestIntent pick can't
    // be trusted to belong to this incoming user - clear it to prevent
    // one customer's date/time selection leaking into another's session.
    if (priorSession) {
      GuestIntent.clear();
    }

    // SECURITY: Also clear any admin sessions (if admin didn't log out)
    AdminStorage.clear();

    // Server has set the httpOnly session cookies — only cache the
    // display object here, never the tokens.
    localStorage.setItem('user', JSON.stringify(response.data.user));
    setUser(response.data.user);
    setIsGuest(false);

    return response.data;
  };

  const signup = async (name, email, phone, password) => {
    const response = await axios.post(`${API}/auth/signup`, { name, email, phone, password });
    // New flow: signup now requires email verification
    // Backend returns: { message, email, requires_verification: true }
    // DO NOT set token/user here - wait for email verification
    return response.data;
  };

  const guestLogin = async (name, countryCode, phone, email, otp) => {
    // Snapshot BEFORE clearAuthOnly() wipes it — see login() above.
    const priorSession = hadPriorCustomerSession();

    const response = await axios.post(`${API}/auth/guest`, { 
      name, 
      country_code: countryCode, 
      phone,
      email,
      otp 
    }, { withCredentials: true });
    
    // CRITICAL FIX: Clear stale auth before setting new guest login.
    // Use clearAuthOnly() so bookingIntent / hoopbook_selection survive -
    // the whole point of the pre-login intent is to resume after login.
    CustomerStorage.clearAuthOnly();

    // SECURITY: same cross-user leak guard as login() above.
    if (priorSession) {
      GuestIntent.clear();
    }

    // SECURITY: Also clear any admin sessions (if admin didn't log out)
    AdminStorage.clear();
    
    // Server has set the httpOnly session cookies — only cache the
    // display object here, never the tokens.
    localStorage.setItem('guest', JSON.stringify(response.data.guest));
    setUser(response.data.guest);
    setIsGuest(true);

    return response.data;
  };

  const requestOTP = async (countryCode, phone, email, name) => {
    const response = await axios.post(`${API}/auth/request-otp`, { 
      country_code: countryCode, 
      phone,
      email,
      name,
    });
    return response.data;
  };

  const logout = async () => {
    try {
      // Cookie carries the refresh token — no body needed.
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {}); // Ignore errors
    } catch (error) {
      // Ignore logout API errors
    }
    
    // Clear localStorage first - full wipe including bookingIntent
    // and hoopbook_selection so the next user starts clean.
    CustomerStorage.clear();
    
    // Use requestAnimationFrame to batch state updates
    // This helps avoid concurrent rendering issues
    requestAnimationFrame(() => {
      setUser(null);
      setIsGuest(false);
    });
  };

  /**
   * Replace the in-memory + persisted user record after a self-service
   * profile edit (name / phone). Keeps every other AuthContext concern
   * untouched — only the `user` value changes.
   */
  const updateUser = (nextUser) => {
    if (!nextUser) return;
    const merged = { ...(user || {}), ...nextUser };
    localStorage.setItem('user', JSON.stringify(merged));
    setUser(merged);
  };

  /**
   * Called by AuthCallback after a successful Google OAuth exchange.
   * Sets the user in context so the app is immediately aware without
   * requiring a full page reload or re-mount.
   */
  const setGoogleUser = (googleUser) => {
    if (!googleUser) return;
    setUser(googleUser);
    setIsGuest(false);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, isGuest, loading, login, signup, guestLogin, requestOTP, logout, updateUser, setGoogleUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};